import { Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';
import { Compbaser } from "../../../components/compbaser";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { LocalStoreService, StoreSelectorsService } from "../../../services";
import { IResourcePreview, IResourceSharedItem, ISharedContent } from "../../../interfaces";
import { getResourceFormat, SUPPORTED_FILE_TYPES, appData, isHostDev } from "../../../common";
import { ActionsSubject } from "@ngrx/store";
import { ofType } from "@ngrx/effects";
import { actionGetResourcesSharedLibrary, actionGetResourcesSharedLibrarySuccess, actionGetSharedCommonContentReply } from "../../../store/actions/user.actions";
import { IDialogGetAsset } from "../../../components/dialog-get-asset/dialog-get-asset.component";
import { ImportService } from "../../../services/import.service";
import { ICategoryCheckItemNode, ICategoryTreeSelection } from "../../../components/category-tree/tree-service/tree-data.model";
import { Plugin } from "@egjs/ngx-flicking";
import { Arrow } from "@egjs/flicking-plugins";
import { Lib } from 'libs/Lib';
import * as _ from 'lodash';

interface IResourceGroup {
    groupName: string;
    resources: IResourcePreview[];
    plugins: Plugin[];
}

@Component({
    selector: 'app-resources-library',
    templateUrl: './resources-library.component.html',
    styleUrls: ['./resources-library.component.scss'],
    standalone: false,
    animations: [
        trigger('groupFade', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(20px)' }),
                animate('400ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('250ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
            ])
        ]),
        trigger('cardStagger', [
            transition(':enter', [
                query('.resource-card', [
                    style({ opacity: 0, transform: 'scale(0.92)' }),
                    stagger(30, [
                        animate('300ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
                    ])
                ], { optional: true })
            ])
        ]),
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('500ms ease-out', style({ opacity: 1 }))
            ])
        ])
    ]
})
export class ResourcesLibraryComponent extends Compbaser implements OnInit {
    @ViewChild("importModal", {read: TemplateRef, static: true}) importModal: TemplateRef<any>;

    @Input() dialogGetAsset: IDialogGetAsset;
    @Input() libraryType: 'shared' | 'categories' = 'categories';
    @Output() resourceSelected = new EventEmitter<any>();

    allResourceGroups: IResourceGroup[] = [];
    resourceGroups: IResourceGroup[] = [];
    isLoading = true;
    selectedResource: IResourcePreview;
    imageLoading = true;
    importModalDialog: any;
    categoryTreeItems: Array<ICategoryCheckItemNode> = [];
    selectedCategories: string[] = [];

    flickConfig = {
        gap: 14,
        circular: false,
        align: 'prev' as const,
        bound: true,
        renderOnlyVisible: true,
    };

    constructor(
        private storeSelector: StoreSelectorsService,
        protected override dialog: MatDialog,
        private actionsSubj: ActionsSubject,
        private importService: ImportService,
        private localStore: LocalStoreService,
        private router: Router,
        private elRef: ElementRef
    ) {
        super(dialog);
        this.getResourcesLibrary();
    }

    ngOnInit() {
        if (this.libraryType === 'categories') {
            this.loadCategorizedResources();
            this.listenForStoredCategoryChanges();
        } else if (this.libraryType === 'shared') {
            this.loadSharedResources();
        }
    }

    private listenForStoredCategoryChanges() {
        this.cod(
            this.storeSelector.getLocalStoreUpdates("ILocalStorageConfig").subscribe((e) => {
                if (!e) return;
                const storedCategories = (this.localStore.getStorageValConfig("resourceCategories") || "").split(";");
                const selectFirst3 = storedCategories.length === 1 && storedCategories[0] === "";

                this.categoryTreeItems.forEach((v) => {
                    v.selected = selectFirst3 ? this.categoryTreeItems.indexOf(v) < 3 : storedCategories.includes(v.item);
                });

                this.selectedCategories = selectFirst3
                    ? this.categoryTreeItems.slice(0, 3).map(v => v.item)
                    : storedCategories.filter((cat: string) => this.categoryTreeItems.some(item => item.item === cat));

                this.applyFilters();
            })
        );
    }

    private getResourcesLibrary() {
        const lib = this.localStore.getSharedResourceLibrary();
        if (lib && !Lib.DevMode() && !isHostDev()) {
            return this.storeSelector.dispatch(actionGetResourcesSharedLibrarySuccess({ value: lib }));
        }
        console.log("getting fresh shared library resources...");
        this.storeSelector.dispatch(
            actionGetResourcesSharedLibrary({
                config: {
                    "ida.signage.me": [],
                },
            }),
        );
    }

    private buildGroup(groupName: string, resources: IResourcePreview[]): IResourceGroup {
        return {
            groupName,
            resources,
            plugins: [new Arrow({ moveCount: 4 })]
        };
    }

    private naturalSort(a: string, b: string): number {
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    }

    private buildCategoryTree(categoryNames: string[]) {
        const sorted = categoryNames.sort((a, b) => this.naturalSort(a, b));
        const storedCategories = (this.localStore.getStorageValConfig("resourceCategories") || "").split(";");
        const selectFirst3 = storedCategories.length === 1 && storedCategories[0] === "";

        const children = sorted.map((name, i) => ({
            id: _.uniqueId('__'),
            item: name,
            children: [],
            selected: selectFirst3 ? i < 3 : storedCategories.includes(name)
        }));

        this.categoryTreeItems = children;
        this.selectedCategories = selectFirst3
            ? sorted.slice(0, 3)
            : storedCategories.filter((cat: string) => sorted.includes(cat));

        // Save initial selection to local storage if empty
        if (selectFirst3) {
            this.updateStoredCategories();
        }
    }

    private updateStoredCategories() {
        const selectedCategories = this.selectedCategories.join(";");
        this.localStore.setStorageValConfig("resourceCategories", selectedCategories || "none");
    }

    private applyFilters() {
        let filtered = this.allResourceGroups;

        if (this.selectedCategories.length === 0) {
            filtered = [];  // Show nothing when no categories selected
        } else if (this.selectedCategories.length < this.allResourceGroups.length) {
            filtered = filtered.filter(g => this.selectedCategories.includes(g.groupName));
        }
        // else if all categories selected, show all groups

        this.resourceGroups = filtered;
    }

    onUpdateTree(categoryTreeSelection: ICategoryTreeSelection) {
        const prevNames = new Set(this.resourceGroups.map(g => g.groupName));
        const categories = [];
        if (categoryTreeSelection.checklist.length > 0) {
            categoryTreeSelection.checklist.forEach(v => {
                if (!v.expandable) categories.push(v.item);
            });
        }
        this.selectedCategories = categories;
        this.updateStoredCategories();
        this.applyFilters();

        const newGroup = this.resourceGroups.find(g => !prevNames.has(g.groupName));
        if (newGroup) {
            setTimeout(() => {
                const container = this.elRef.nativeElement.querySelector('.resources-library-container');
                const groupEl = this.elRef.nativeElement.querySelector(`[data-group="${newGroup.groupName}"]`);
                if (container && groupEl) {
                    const top = groupEl.offsetTop - container.offsetTop;
                    container.scrollTo({ top, behavior: 'smooth' });
                }
            }, 50);
        }
    }

    private loadCategorizedResources() {
        this.isLoading = true;
        this.cod(
            this.storeSelector.getResourceSharedLibrary().subscribe((v) => {
                this.localStore.setStorageSharedLibrary(v);
                if (!v.library || v.library.length === 0) {
                    return;
                }

                const resourcesByCategory: { [key: string]: IResourcePreview[] } = {};

                v.library.forEach((r: IResourceSharedItem) => {
                    if (SUPPORTED_FILE_TYPES.indexOf(r.$.resourceType.toLowerCase()) === -1) return;

                    const treePath = r.$.treePath || 'Uncategorized';
                    if (!resourcesByCategory[treePath]) {
                        resourcesByCategory[treePath] = [];
                    }

                    const res = getResourceFormat(r.$);
                    res.resourceId = r.$.resourceId;
                    res.businessId = r.$.businessId;
                    res.name = r.$.resourceName;
                    res.treePath = r.$.treePath;
                    res.resourceType = r.$.resourceType;
                    if (!res.url) res.url = r.url;
                    res.show = true;
                    if (res.resourceType === 'mp4') {
                        res.videoUrl = r.url;
                    }

                    resourcesByCategory[treePath].push(res);
                });

                this.allResourceGroups = Object.keys(resourcesByCategory)
                    .sort((a, b) => this.naturalSort(a, b))
                    .map(categoryName => this.buildGroup(categoryName, resourcesByCategory[categoryName]));

                this.buildCategoryTree(Object.keys(resourcesByCategory));
                this.applyFilters();
                this.isLoading = false;
            })
        );
    }

    private loadSharedResources() {
        this.isLoading = true;
        this.cod(
            this.actionsSubj.pipe(ofType(actionGetSharedCommonContentReply)).subscribe((reply: any) => {
                const i_sharedContents: ISharedContent[] = reply.value;

                if (!i_sharedContents || i_sharedContents.length === 0) {
                    this.isLoading = false;
                    this.allResourceGroups = [];
                    this.resourceGroups = [];
                    return;
                }

                const resourcesByBusinessId: { [key: string]: IResourcePreview[] } = {};

                i_sharedContents.forEach((item) => {
                    const sceneContent = item.data?.resources;
                    if (sceneContent?.length > 0) {
                        sceneContent.forEach((row) => {
                            if (SUPPORTED_FILE_TYPES.indexOf(row.type.toLowerCase()) === -1) return;

                            const businessId = row.businessId + "";
                            if (!resourcesByBusinessId[businessId]) {
                                resourcesByBusinessId[businessId] = [];
                            }

                            const sharedItem: IResourceSharedItem = {
                                $: {
                                    businessId: row.businessId + "",
                                    resourceId: row.resourceId + "",
                                    resourceType: row.type,
                                    resourceName: row.name,
                                    treePath: row.path,
                                    defaultPlayer: "",
                                },
                                url: `${appData.cloudResourcesS3}/business${row.businessId}/resources/${row.resourceId}.${row.type}`,
                            };

                            const res = getResourceFormat(sharedItem.$);
                            res.resourceId = sharedItem.$.resourceId;
                            res.businessId = sharedItem.$.businessId;
                            res.name = sharedItem.$.resourceName;
                            res.treePath = sharedItem.$.treePath;
                            res.resourceType = sharedItem.$.resourceType;
                            if (!res.url) res.url = sharedItem.url;
                            res.show = true;
                            if (res.resourceType === 'mp4') {
                                res.videoUrl = sharedItem.url;
                            }

                            resourcesByBusinessId[businessId].push(res);
                        });
                    }
                });

                this.allResourceGroups = Object.keys(resourcesByBusinessId)
                    .sort((a, b) => this.naturalSort(a, b))
                    .map(businessId => this.buildGroup(`Business ID: ${businessId}`, resourcesByBusinessId[businessId]));

                this.resourceGroups = this.allResourceGroups;
                this.isLoading = false;
            })
        );
    }

    openImportDialog(resource: IResourcePreview) {
        this.selectedResource = resource;
        this.imageLoading = true;
        this.importModalDialog = this.dialog.open(this.importModal, {
            width: '850px',
            height: '80%'
        });
        this.importModalDialog.afterClosed().subscribe(() => {
            this.imageLoading = true;
        });
    }

    async onImportClick() {
        if (!this.selectedResource) return;
        this.imageLoading = true;
        const newResourceId = await this.importService.importResource(this.selectedResource.businessId, this.selectedResource.resourceId);

        if (this.resourceSelected) {
            this.resourceSelected.emit(newResourceId);
        }

        if (!this.dialogGetAsset) {
            this.router.navigateByUrl('/resources/list');
        }

        setTimeout(() => {
            this.onClose();
        }, 333);
    }

    onClose() {
        this.imageLoading = true;
        if (this.importModalDialog) {
            this.importModalDialog.close();
        }
    }

    onImageLoad() {
        this.imageLoading = false;
    }
}
